import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  Circle,
  Monitor,
  Server,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_BADGE = {
  frontend: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  backend: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

function statusBadgeClass(statusCode) {
  if (statusCode >= 500) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  if (statusCode >= 400) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const hasOffset = iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso);
  const s = hasOffset ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Mexico_City',
  });
}

// ─── Fila de error individual (expandible con el detalle completo) ────
function ErrorDetailRow({ err, onResolve, resolving }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-b last:border-0">
      <button
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium shrink-0 ${SOURCE_BADGE[err.source] || SOURCE_BADGE.backend}`}>
            {err.source === 'frontend' ? <Monitor className="h-3 w-3" /> : <Server className="h-3 w-3" />}
            {err.source}
          </span>
          {err.status_code != null && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium shrink-0 ${statusBadgeClass(err.status_code)}`}>
              {err.status_code}
            </span>
          )}
          <span className="text-sm truncate">{err.error_message}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {err.is_resolved ? (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> Resuelto
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Circle className="h-3.5 w-3.5" /> Pendiente
            </span>
          )}
          <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(err.created_at)}</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 bg-muted/20">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs pt-3">
            <div><span className="text-muted-foreground">Endpoint:</span> {err.endpoint || '—'}</div>
            <div><span className="text-muted-foreground">Método:</span> {err.http_method || '—'}</div>
            <div><span className="text-muted-foreground">Status:</span> {err.status_code ?? '—'}</div>
            <div><span className="text-muted-foreground">Fecha:</span> {fmtDate(err.created_at)}</div>
            {err.is_resolved && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Resuelto por:</span> {err.resolved_by || '—'} — {fmtDate(err.resolved_at)}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1">Mensaje completo</p>
            <pre className="text-xs bg-background border rounded p-2 whitespace-pre-wrap break-words">{err.error_message}</pre>
          </div>

          {err.stack_trace && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">
                {err.source === 'frontend' ? 'Stack trace / component stack' : 'Traceback'}
              </p>
              <pre className="text-xs bg-background border rounded p-2 whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                {err.stack_trace}
              </pre>
            </div>
          )}

          {err.extra_context && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Contexto adicional</p>
              <pre className="text-xs bg-background border rounded p-2 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                {JSON.stringify(err.extra_context, null, 2)}
              </pre>
            </div>
          )}

          <div className="pt-1">
            <Button
              size="sm"
              variant={err.is_resolved ? 'outline' : 'default'}
              disabled={resolving}
              onClick={() => onResolve(err.id, !err.is_resolved)}
            >
              {resolving && <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {err.is_resolved ? 'Marcar como pendiente' : 'Marcar como resuelto'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Grupo por agente (expandible, con su propia paginación) ──────────
const AGENT_ERRORS_PAGE_SIZE = 15;

function AgentGroup({ group, filters, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [resolvingId, setResolvingId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / AGENT_ERRORS_PAGE_SIZE));

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/error-logs`, {
        params: {
          page: p,
          page_size: AGENT_ERRORS_PAGE_SIZE,
          agent_name: group.user_id ? group.agent_name : '__none__',
          source: filters.source,
          is_resolved: filters.is_resolved,
          start: filters.start,
          end: filters.end,
        },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {
      toast.error('Error al cargar los errores de este agente');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.agent_name, group.user_id, filters.source, filters.is_resolved, filters.start, filters.end]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) load(1);
  };

  const handleResolve = async (errorId, resolve) => {
    setResolvingId(errorId);
    try {
      await axios.patch(`${API}/error-logs/${errorId}/resolve`, { is_resolved: resolve });
      toast.success(resolve ? 'Marcado como resuelto' : 'Marcado como pendiente');
      await load(page);
      onChanged?.();
    } catch {
      toast.error('Error al actualizar el estado');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors text-left"
        onClick={toggle}
      >
        <span className="font-medium text-sm">{group.agent_name}</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
            Último: {fmtDate(group.latest_error_at)}
          </span>
          <span className="inline-flex items-center rounded-full bg-[#1a365d] text-white px-2.5 py-0.5 text-xs font-semibold">
            {group.count}
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>
      {expanded && (
        <CardContent className="p-0 border-t">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a365d]" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">Sin errores con este filtro</p>
          ) : (
            <>
              {items.map(err => (
                <ErrorDetailRow
                  key={err.id}
                  err={err}
                  onResolve={handleResolve}
                  resolving={resolvingId === err.id}
                />
              ))}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 py-2 border-t">
                  <Button variant="outline" size="sm" disabled={page === 1}
                    onClick={() => load(page - 1)} className="h-7 text-xs px-2">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page === totalPages}
                    onClick={() => load(page + 1)} className="h-7 text-xs px-2">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Página principal ───────────────────────────────────────────────
const GROUPS_PAGE_SIZE = 20;

export function ErrorsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !['admin', 'master'].includes(user.role)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const [source, setSource] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('unresolved'); // 'unresolved' | 'resolved' | 'all'
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / GROUPS_PAGE_SIZE));

  const filters = {
    source: source === 'all' ? undefined : source,
    is_resolved: resolvedFilter === 'all' ? undefined : resolvedFilter === 'resolved',
    start: start || undefined,
    end: end || undefined,
  };

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/error-logs/by-agent`, {
        params: { ...filters, q: q || undefined, page: p, page_size: GROUPS_PAGE_SIZE },
      });
      setGroups(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {
      toast.error('Error al cargar el panel de errores');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, resolvedFilter, start, end, q]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div className="space-y-6" data-testid="errors-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Panel de errores</h1>
        <p className="text-muted-foreground">
          Errores reportados por agentes (frontend y backend), agrupados por quién los tuvo
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fuentes</SelectItem>
            <SelectItem value="frontend">Frontend</SelectItem>
            <SelectItem value="backend">Backend</SelectItem>
          </SelectContent>
        </Select>

        <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unresolved">No resueltos</SelectItem>
            <SelectItem value="resolved">Resueltos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Desde</span>
          <input
            type="date"
            value={start}
            onChange={e => setStart(e.target.value)}
            className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Hasta</span>
          <input
            type="date"
            value={end}
            onChange={e => setEnd(e.target.value)}
            className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Buscar agente..."
            value={qInput}
            onChange={e => setQInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setQ(qInput); }}
            className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9 min-w-[180px]"
          />
          <Button variant="outline" size="sm" onClick={() => setQ(qInput)}>Buscar</Button>
        </div>

        <Button variant="outline" size="sm" onClick={() => load(page)} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground/30" />
            <div>
              <p className="font-medium text-muted-foreground">Sin errores con este filtro</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Ajusta los filtros o el rango de fechas</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map(g => (
            <AgentGroup
              key={g.user_id || g.agent_name}
              group={g}
              filters={filters}
              onChanged={() => load(page)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => load(page - 1)} className="gap-1">
            <ChevronLeft className="h-4 w-4" /> Anterior
          </Button>
          <span className="text-sm text-muted-foreground whitespace-nowrap">Página {page} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => load(page + 1)} className="gap-1">
            Siguiente <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
